import React, {useEffect, useMemo, useRef, useState} from "react";
import useConfirmation from '../common/useConfirmation';
import { interfaceError } from '../../i18n/errorMessages';
import { message as interfaceMessage } from '../../i18n/messages';
import LocalizedMessage from '../common/LocalizedMessage';
import toast from '../../i18n/toast';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory, useLocation } from 'react-router-dom';
import AuditHistoryLink from './AuditHistoryLink';
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import API from "../../services/SchoolClassService";
import SettingsService from "../../services/SettingsService";
import SchoolCalendarService from "../../services/SchoolCalendarService";
import SchoolClassForm from "./SchoolClassForm";
import { isValidDateValue } from "../common/PolishDateInput";
import {ROLES, STATUS, WEEKDAYS} from "../common/Const";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  getConfiguredLessonTypeOptions,
  getLessonTypeById,
  getSuggestedLessonType,
  getLessonTypesSetting,
} from "../common/lessonTypes";
import {mandatory, validateValues} from "../common/Validations";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as bookActions from "../../redux/actions/bookActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as clientActions from "../../redux/actions/clientActions";
import {
  findDayOfWeek,
  getById,
  hasErrors,
  hasNoErrors,
  minutesToTimeString,
  timeStringToMinutes,
  getStudentsInThisClass,
} from "../common/Helpers";
import LessonsList from "./LessonsList";
import StudentsList from "./StudentsList";
import ClassStudentPickerModal from "./ClassStudentPickerModal";
import {
  findOverlapSchoolClass as findOverlappingSchoolClass,
  saveSchoolClassWithAttendanceGuard,
} from "./schoolClassScheduling";
import {
  buildManageRouteWithSchoolYear,
} from "./manageSchoolYearRouting";
import { buildSchoolYearRange } from "../common/schoolYear";
import { buildSchoolClassReturnRoute } from './studentClassesNavigation';
import { canManageClasses, getEffectivePermissionRole } from "./rbac";
import { useTeacherAccess, scopeAllowsTeacher } from './TeacherAccessProvider';
import TeacherAccessService from '../../services/TeacherAccessService';
import { buildDashboardReturnRoute, getPrincipalKey, readCreationContext } from './dashboardClassCreationNavigation';
import { useTranslation } from 'react-i18next';

const { allowsGroupExpansion } = require("../../../LambdaFunctions/shared/publicTimetablePolicy");

const EMPTY_SCHOOL_CLASS = { isRecurring: true };

const validationConfig = {
  classTypeId: {
    label: "Typ zajęć",
    validate: [mandatory],
  },
  dayOfWeek: {
    label: "Dzień tygodnia",
    validate: [mandatory],
  },
  startTime: {
    label: "Początek zajęć",
    validate: [mandatory],
  },
  teacherId: {
    label: "Nauczyciel",
    validate: [mandatory],
  },
};

function getName(person) {
  return person ? `${person.firstName || ''} ${person.lastName || ''}` : '';
}

export function SchoolClass({
  schoolClasses, loadSchoolClasses, teachers, loadTeachers, saveSchoolClass, scheduledLessons,
  students, loadStudents, loadClassStudents, studentsInThisClass, studentsNotInThisClass,
  addClassStudent, removeClassStudent, saveLessonRegister, loadLessonRegisters, deleteLessonRegister, books, loadBooks,
  clients, loadClients,
  activeSchoolYearStartYear,
  userRole,
  userTeacherId,
  getSettings,
  principalKey,
  ...props
}) {
  const { confirm, confirmation } = useConfirmation();
  const { t: tr } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isNewClass = props.match.params.id === 'new';
  const { extensions } = useTeacherAccess();
  const canCreateClass = canManageClasses(userRole) || extensions.classesManageScope !== 'none';
  const creationContext = useMemo(() => (
    isNewClass && canCreateClass ? readCreationContext(location.state, principalKey) : null
  ), [isNewClass, location.state, principalKey, canCreateClass]);
  const draftKey = `${location.key || ''}:${principalKey}`;
  const initializedDraft = useRef(null);
  const editEntry = useRef(null);
  const endDateEdited = useRef(false);
  const saving = useRef(false);
  const activePrincipal = useRef(principalKey);
  activePrincipal.current = principalKey;
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const isTeacher = userRole === ROLES.TEACHER;
  // eslint-disable-next-line react/destructuring-assignment
  const [schoolClass, setSchoolClass] = useState({ ...props.schoolClass, ...(creationContext ? creationContext.prefill : {}) });
  const canManageClass = !isTeacher || (isNewClass ? canCreateClass : scopeAllowsTeacher(extensions.classesManageScope, userTeacherId, schoolClass.teacherId));
  const canAssignStudents = !isTeacher || scopeAllowsTeacher(extensions.classesManageScope, userTeacherId, schoolClass.teacherId);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidateCursor, setCandidateCursor] = useState(null);
  const candidateGeneration = useRef(0);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(STATUS.VIEW);
  const [showSelectStudentModal, setShowSelectStudentModal] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [addStudentError, setAddStudentError] = useState("");
  const addingStudentRef = useRef(false);
  const [dateReadOnly, setDateReadOnly] = useState(false);
  const [classTypes, setClassTypes] = useState(getConfiguredLessonTypeOptions([], schoolClass.classTypeId));
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  // Existing records retain the original type's inferred policy even while changing type.
  const expansionTypeId = isNewClass ? schoolClass.classTypeId : props.schoolClass.classTypeId;
  const effectiveExpansion = allowsGroupExpansion(schoolClass, getLessonTypeById(lessonTypes, expansionTypeId));

  useEffect(() => {
    if (isNewClass && isTeacher && extensions.classesManageScope === 'own') {
      setSchoolClass(current => ({ ...current, teacherId: userTeacherId }));
    }
  }, [isNewClass, isTeacher, extensions.classesManageScope, userTeacherId]);

  useEffect(() => {
    const generation = ++candidateGeneration.current;
    if (!isTeacher || !showSelectStudentModal || !canAssignStudents) return undefined;
    const timeout = setTimeout(() => {
      TeacherAccessService.getCandidates(schoolClass.id, candidateQuery).then(result => {
        if (generation !== candidateGeneration.current) return;
        setCandidateOptions(result.items.map(item => ({ value: item.id, text: `${item.firstName} ${item.lastName} (${item.disambiguation})` })));
        setCandidateCursor(result.cursor);
      }).catch(error => { if (generation === candidateGeneration.current) setAddStudentError(error.message); });
    }, 200);
    return () => { clearTimeout(timeout); candidateGeneration.current += 1; };
  }, [isTeacher, showSelectStudentModal, canAssignStudents, schoolClass.id, candidateQuery]);

  const loadMoreCandidates = async () => {
    const generation = candidateGeneration.current;
    try {
      const result = await TeacherAccessService.getCandidates(schoolClass.id, candidateQuery, candidateCursor);
      if (generation !== candidateGeneration.current) return;
      setCandidateOptions(current => [...current, ...result.items.map(item => ({ value: item.id, text: `${item.firstName} ${item.lastName} (${item.disambiguation})` }))]);
      setCandidateCursor(result.cursor);
    } catch (error) { setAddStudentError(error.message); }
  };


  useEffect(() => {
    Promise.resolve()
      .then(() => getSettings())
      .then((settings) => {
        setClassTypes(getConfiguredLessonTypeOptions(settings, schoolClass.classTypeId));
        setLessonTypes(getLessonTypesSetting(settings));
      })
      .catch(() => {
        setClassTypes(getConfiguredLessonTypeOptions([], schoolClass.classTypeId));
        setLessonTypes(DEFAULT_LESSON_TYPES_SETTING);
      });
  }, [getSettings, schoolClass.classTypeId]);

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    if (isNewClass) {
      if (initializedDraft.current !== draftKey) {
        saving.current = false;
        endDateEdited.current = false;
        setSchoolClass({ ...EMPTY_SCHOOL_CLASS,
          ...(creationContext ? creationContext.prefill : {}),
          ...(isTeacher && extensions.classesManageScope === 'own' ? { teacherId: userTeacherId } : {}) });
        setErrors({});
        setTouched({});
        setStatus(STATUS.IDLE);
        initializedDraft.current = draftKey;
        if (creationContext) window.scrollTo(0, 0);
      }
      return;
    }
    setSchoolClass({...props.schoolClass});
    if (props.schoolClass.id) {
      const entryKey = `${location.key || ''}:${principalKey}:${props.schoolClass.id}`;
      const permitted = canManageClasses(userRole) || (isTeacher && scopeAllowsTeacher(extensions.classesManageScope, userTeacherId, props.schoolClass.teacherId));
      const openEdit = permitted && location.state && location.state.editClassId === props.schoolClass.id && editEntry.current !== entryKey;
      if (openEdit) editEntry.current = entryKey;
      setStatus(current => (openEdit || (permitted && editEntry.current === entryKey && current === STATUS.IDLE) ? STATUS.IDLE : STATUS.VIEW));
    } else {
      setStatus(STATUS.IDLE);
    }
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.schoolClass, isNewClass, draftKey, creationContext, isTeacher, extensions.classesManageScope, userTeacherId, location.key, location.state, principalKey, userRole]);

  useEffect(() => {
    if (!isNewClass || !schoolClass.isRecurring) return undefined;
    let cancelled = false;
    SchoolCalendarService.getCalendar(activeSchoolYearStartYear).then(calendar => {
      if (cancelled || endDateEdited.current || saving.current) return;
      const tillDate = calendar.teachingPeriod && calendar.teachingPeriod.tillDate;
      if (isValidDateValue(tillDate)) {
        setSchoolClass(current => ({ ...current, tillDate }));
      }
    }).catch(() => {
      // A calendar is an optional default; dates can still be entered manually.
    });
    return () => { cancelled = true; };
  }, [isNewClass, schoolClass.isRecurring, activeSchoolYearStartYear, draftKey]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, []);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (isTeacher || students.length === 0) {
      loadStudents().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [isTeacher, loadStudents]);

  useEffect(() => {
    if (!isTeacher && books.length === 0) {
      loadBooks().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [books.length, isTeacher, loadBooks]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    if (canManageClass && clients.length === 0) {
      loadClients().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [canManageClass, clients.length, loadClients]);

  useEffect(() => {
    let hasNonEmptyLessonRegister = false;

    scheduledLessons.forEach((lesson) => {
      if (lesson.lessonRegister !== null) {
        hasNonEmptyLessonRegister = true;
      }
    });

    setDateReadOnly(hasNonEmptyLessonRegister);
  }, [scheduledLessons]);

  function getSchoolClassName(aSchoolClass) {
    const {startTime, dayOfWeek, classTypeId} = aSchoolClass;
    const dayName = findDayOfWeek(dayOfWeek).name;
    const selectedClassType = getLessonTypeById(lessonTypes, classTypeId);
    const startTimeMinutes = timeStringToMinutes(startTime);
    const endTime = (selectedClassType && startTimeMinutes)
      ? minutesToTimeString(startTimeMinutes + selectedClassType.durationMinutes)
      : "";
    return `${dayName} ${selectedClassType ? selectedClassType.name : classTypeId} ${startTime}-${endTime}`;
  }

  function findOverlapSchoolClass() {
    return findOverlappingSchoolClass(schoolClass, schoolClasses, undefined, lessonTypes);
  }

  const validate = () => {
    const {isRecurring, dayOfWeek, fromDate, tillDate} = schoolClass;
    const fromDateDayOfWeek = WEEKDAYS.find(d => d.day === (new Date(fromDate)).getDay());
    // eslint-disable-next-line no-shadow
    const errors = validateValues(schoolClass, validationConfig);
    if ((schoolClass.publicLevel || '').length > 80) errors.publicLevel = tr('manage:publicLevelTooLong');
    if (!fromDate) {
      errors.fromDate = tr("manage:thisFieldIsRequired_287");
    }
    if (fromDate && !isValidDateValue(fromDate)) errors.fromDate = tr("manage:enterAValidDateInDDMMYYYYFormat_205");
    if (isRecurring && tillDate && !isValidDateValue(tillDate)) errors.tillDate = tr("manage:enterAValidDateInDDMMYYYYFormat_205");
    if (!isRecurring && dayOfWeek && fromDate && (!fromDateDayOfWeek || fromDateDayOfWeek.id !== dayOfWeek)) {
      errors.dayOfWeek = tr("manage:theDayOfTheWeekDoesNotMatchThe_288");
    }
    if (isRecurring && !tillDate) {
      errors.tillDate = tr("manage:thisFieldIsRequired_287");
    }
    if (isRecurring && isValidDateValue(fromDate) && isValidDateValue(tillDate) && fromDate > tillDate) {
      errors.fromDate = tr("manage:theClassEndDateCannotBeEarlierThanThe_289");
      errors.tillDate = tr("manage:theClassEndDateCannotBeEarlierThanThe_289");
    }
    // check teacher does not have any other class at the same time
    const overlapped = !errors.fromDate && !errors.tillDate ? findOverlapSchoolClass() : null;
    if (overlapped) {
      const message = interfaceMessage('manage:classOverlap', { name: getSchoolClassName(overlapped) });
      errors.saveError = message;
    }
    // check that if class is not recurrent than fromDate must be equal to tillDate
    // check that if class is  recurrent than fromDate must be before tillDate and both must be populated
    setErrors(errors);
    return hasNoErrors(errors);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validate();
  };


  const handleCancel = () => {
    if (saving.current) return;
    setErrors({});
    if (creationContext) {
      history.replace(buildDashboardReturnRoute(creationContext, location.search));
      return;
    }
    if (status === STATUS.VIEW || !schoolClass.id) {
      const from = location.state && location.state.from;
      history.push(buildSchoolClassReturnRoute(from, location.search, activeSchoolYearStartYear));
    } else {
      setSchoolClass(props.schoolClass);
      setStatus(STATUS.VIEW);
    }
  };

  // const handleCancel = () => {
  //   if (status === STATUS.VIEW || !schoolClass.id) {
  //     history.push('/manage/classes');
  //   } else {
  //     setSchoolClass(props.schoolClass);
  //     setStatus(STATUS.VIEW);
  //   }
  // };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "tillDate") endDateEdited.current = true;
    setSchoolClass(prevClass => ({...prevClass, [name]: value}));
    validate();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving.current || !canManageClass) return;
    saving.current = true;
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      // eslint-disable-next-line no-alert
      const confirmAttendanceBackfill = () => confirm(
        tr("manage:theDayOfTheWeekOrStartTimeHas_290"),
      );

      const normalizedClass = {
        ...schoolClass,
        allowGroupExpansion: effectiveExpansion,
        publicLevel: (schoolClass.publicLevel || '').trim(),
        publicCategoryId: schoolClass.publicCategoryId || '',
        ...(!schoolClass.isRecurring ? { tillDate: schoolClass.fromDate } : {}),
      };
      const organizationalClass = Object.fromEntries([
        'id', 'classTypeId', 'dayOfWeek', 'startTime', 'teacherId', 'bookId', 'isRecurring', 'fromDate', 'tillDate', 'comment', 'allowGroupExpansion', 'publicLevel', 'publicAge', 'publicCategoryId',
      ].filter(key => normalizedClass[key] !== undefined).map(key => [key, normalizedClass[key]]));
      const save = isTeacher ? saveSchoolClass(organizationalClass) : saveSchoolClassWithAttendanceGuard({
        originalSchoolClass: props.schoolClass,
        schoolClass: normalizedClass,
        scheduledLessons,
        saveLessonRegister,
        deleteLessonRegister,
        saveSchoolClass,
        confirmAttendanceBackfill,
      });
      save.then((savedClass) => {
        if (!mounted.current || activePrincipal.current !== principalKey) return;
        saving.current = false;
        setStatus(STATUS.VIEW);
        if (creationContext && !schoolClass.id) {
          history.replace(buildDashboardReturnRoute(creationContext, location.search, {
            kind: 'created',
            classId: savedClass.id,
            teacherId: savedClass.teacherId,
            classTypeId: savedClass.classTypeId,
            fromDate: savedClass.fromDate,
            tillDate: savedClass.tillDate,
            dayOfWeek: savedClass.dayOfWeek,
            startTime: savedClass.startTime,
          }));
          return;
        }
        if (!schoolClass.id) {
          history.push(buildManageRouteWithSchoolYear(`/manage/class/${savedClass.id}`, location.search, activeSchoolYearStartYear));
        }
      })
        .catch((error) => {
          if (!mounted.current || activePrincipal.current !== principalKey) return;
          saving.current = false;
          setErrors({
            ...errors,
            saveError: isTeacher && error.code === 403
              ? interfaceMessage('manage:teacherExtensions.accessChanged') : interfaceError(error, 'common:errors.save'),
          });
          setStatus(STATUS.SUBMITTED);
        });
    } else {
      saving.current = false;
      setStatus(STATUS.SUBMITTED);
    }
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then(() => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleEdit = () => {
    setErrors({});
    setStatus(STATUS.IDLE);
  };

  const handleOpenStudentPicker = () => {
    if (!canAssignStudents || !schoolClass.id || addingStudentRef.current) return;
    setAddStudentError("");
    setShowSelectStudentModal(true);
  };

  const handleCloseStudentPicker = () => {
    if (addingStudentRef.current) return;
    setShowSelectStudentModal(false);
    setAddStudentError("");
  };

  const handleAddStudent = async (student) => {
    if (!canAssignStudents || !schoolClass.id || addingStudentRef.current) return;
    if (!(isTeacher ? candidateOptions : studentsNotInThisClass).some(option => option.value === student.value)) return;
    addingStudentRef.current = true;
    setIsAddingStudent(true);
    setAddStudentError("");
    try {
      const savedClass = schoolClasses.find(item => item.id === schoolClass.id) || schoolClass;
      const type = getLessonTypeById(lessonTypes, savedClass.classTypeId);
      const count = studentsInThisClass.length + 1;
      const suggestedType = getSuggestedLessonType(lessonTypes, type.classTypeId, count);
      const messageKey = suggestedType ? 'manage:classCapacitySuggestion' : 'manage:classCapacityConfirmation';
      if (type.maxStudents && count > type.maxStudents && !await confirm(interfaceMessage(messageKey, {
        name: type.name, count, max: type.maxStudents, suggestedName: suggestedType && suggestedType.name,
      }))) return;
      await addClassStudent({ schoolClassId: schoolClass.id, studentId: student.value });
      setShowSelectStudentModal(false);
      toast.success(tr("manage:extraStudentAdded_77"));
    } catch (error) {
      setAddStudentError((error && error.message) || tr("manage:couldNotAddTheStudentPleaseTryAgain_291"));
    } finally {
      addingStudentRef.current = false;
      setIsAddingStudent(false);
    }
  };

  const handleRemoveStudent = (student) => {
    if (!canAssignStudents) return;
    const {classStudent} = student;
    removeClassStudent(classStudent)
      .then(() => {
        setShowSelectStudentModal(false);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
      });
  };

  if (!isNewClass && !props.schoolClass.id) {
    return (
      <div className="mb-5">
        <div className="alert alert-info" role="alert">{tr('manage:classUnavailable')}</div>
        <button type="button" className="btn manage-button manage-teal-button" onClick={handleCancel}>{tr('common:back')}</button>
      </div>
    );
  }

  return (
    <>{confirmation}<div className="mb-5">
      {!isNewClass && <div className="mb-3"><AuditHistoryLink entityId={schoolClass.id} userRole={userRole} /></div>}
      { hasErrors(errors) && (status === STATUS.SUBMITTED || errors.saveError) && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).filter(key => status === STATUS.SUBMITTED || key === 'saveError')
            .map(key => <div key={key}><LocalizedMessage isError value={errors[key]} /></div>)}
        </div>
      )}

      <SchoolClassForm
        submitLabel={creationContext ? tr("manage:saveAndReturnToTimetable_292") : undefined}
        cancelLabel={creationContext ? tr("common:cancel") : undefined}
        classTypes={classTypes}
        teachers={isTeacher && extensions.classesManageScope === 'own' ? teachers.filter(teacher => teacher.id === userTeacherId) : teachers}
        weekdays={WEEKDAYS}
        books={books}
        id={schoolClass.id}
        teacherId={schoolClass.teacherId}
        classTypeId={schoolClass.classTypeId}
        bookId={schoolClass.bookId}
        dayOfWeek={schoolClass.dayOfWeek}
        comment={schoolClass.comment}
        allowGroupExpansion={effectiveExpansion}
        publicLevel={schoolClass.publicLevel}
        publicCategoryId={schoolClass.publicCategoryId}
        levelOptions={Array.from(new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', ...schoolClasses.map(item => item.publicLevel).filter(Boolean)]))}
        startTime={schoolClass.startTime}
        isRecurring={schoolClass.isRecurring}
        fromDate={schoolClass.fromDate}
        tillDate={schoolClass.tillDate}
        touched={touched}
        errors={errors}
        onBlur={handleBlur}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onEdit={handleEdit}
        onCancel={handleCancel}
        status={status}
        dateReadOnly={dateReadOnly}
        canEdit={canManageClass}
        onAddStudent={canAssignStudents && status !== STATUS.VIEW ? handleOpenStudentPicker : null}
      />

      <ClassStudentPickerModal
        show={showSelectStudentModal}
        options={isTeacher ? candidateOptions : studentsNotInThisClass}
        serverSearch={isTeacher}
        query={candidateQuery}
        onQueryChange={setCandidateQuery}
        onLoadMore={candidateCursor ? loadMoreCandidates : null}
        isSubmitting={isAddingStudent}
        error={addStudentError}
        onSelect={handleAddStudent}
        onHide={handleCloseStudentPicker}
      />

      {status === STATUS.VIEW && (
        <StudentsList
          students={studentsInThisClass}
          onViewParent={() => {}}
          onEdit={null}
          onRemove={canAssignStudents ? handleRemoveStudent : null}
          onAddStudent={canAssignStudents ? handleOpenStudentPicker : null}
          hideClientIdFallback={isTeacher}
        />
      )}
      {status !== STATUS.VIEW && schoolClass.id && canManageClass && (
        <StudentsList
          students={studentsInThisClass}
          onViewParent={() => {}}
          onEdit={null}
          onRemove={canAssignStudents ? handleRemoveStudent : null}
          onAddStudent={canAssignStudents ? handleOpenStudentPicker : null}
        />
      )}

      { schoolClass.id && (
        <LessonsList
          scheduledLessons={scheduledLessons}
          students={students}
          teachers={teachers}
          classTypes={classTypes}
          lessonTypes={lessonTypes}
          onSubmit={handleRegisterUpdate}
          deleteLessonRegister={deleteLessonRegister}
          role={userRole}
          userTeacherId={userTeacherId}
        />
      )}
    </div></>
  );
}

SchoolClass.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired }).isRequired,
  principalKey: PropTypes.string,
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  schoolClass: PropTypes.object.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  saveSchoolClass: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  scheduledLessons: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  loadStudents: PropTypes.func.isRequired,
  addClassStudent: PropTypes.func.isRequired,
  removeClassStudent: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  studentsInThisClass: PropTypes.array.isRequired,
  studentsNotInThisClass: PropTypes.array.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  books: PropTypes.array.isRequired,
  loadBooks: PropTypes.func.isRequired,
  clients: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  getSettings: PropTypes.func,
};

SchoolClass.defaultProps = {
  principalKey: '',
  userRole: "",
  userTeacherId: "",
  getSettings: SettingsService.getSettings,
};

export function getStudentsNotInThisClass(students, classStudents, schoolClassId, clients) {
  const studentIds = (classStudents || []).filter(cs => cs.schoolClassId === schoolClassId).map(cs => cs.studentId);
  return (students || [])
    .filter(s => !s.deleted && !s.archived && !studentIds.includes(s.id))
    .map(s => {
      let clientName = '';
      if (s.clientId && clients) {
        const client = clients.find(c => c.id === s.clientId);
        if (client) {
          clientName = client.firstName && client.lastName
            ? `${client.firstName} ${client.lastName}`
            : client.firstName || s.clientId;
        } else if (s.clientDisplayName) {
          clientName = s.clientDisplayName;
        } else {
          clientName = s.clientId;
        }
      } else if (s.clientDisplayName) {
        clientName = s.clientDisplayName;
      }
      return {
        value: s.id,
        text: clientName ? `${getName(s)} [${clientName}]` : getName(s),
        primaryText: getName(s),
        secondaryText: clientName ? `Klient: ${clientName}` : "",
      };
    });
}

export const mapStateToProps = (state, ownProps) => {
  const {id} = ownProps.match.params;
  const schoolClass =
    id === "new" || state.schoolClasses.length === 0
      ? EMPTY_SCHOOL_CLASS
      : getById(state.schoolClasses, id) || EMPTY_SCHOOL_CLASS;

  const scheduledLessons = API.generateScheduledClasses(schoolClass, state.lessonRegister, state.students, state.classStudents);

  const studentsInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];
  (state.classStudents || []).filter(item => item.schoolClassId === schoolClass.id && item.studentName).forEach((item) => {
    if (!studentsInThisClass.some(student => student.id === item.studentId)) {
      studentsInThisClass.push({ id: item.studentId, ...item.studentName, classStudent: item });
    }
  });
  const studentsNotInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsNotInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];

  return {
    schoolClass,
    schoolClasses: state.schoolClasses,
    teachers: state.teachers,
    students: state.students,
    books: state.books,
    scheduledLessons,
    classStudents: state.classStudents,
    studentsInThisClass,
    studentsNotInThisClass,
    lessonRegister: state.lessonRegister,
    clients: state.clients,
    activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
    userRole: getEffectivePermissionRole(state.user),
    principalKey: getPrincipalKey(state.user),
    userTeacherId: state.user.teacherId,
  };
};

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadClassStudents: classStudentsActions.loadClassStudents,
  addClassStudent: classStudentsActions.addClassStudent,
  removeClassStudent: classStudentsActions.removeClassStudent,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadBooks: bookActions.loadBooks,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
  loadClients: clientActions.loadClients,
};

export default connect(mapStateToProps, mapDispatchToProps)(SchoolClass);
